import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  H3, 
  Intent, 
  Tag, 
  Callout,
  HTMLSelect,
  FormGroup,
  NumericInput
} from '@blueprintjs/core';
import { updateEloForRace } from '../firebase/eloService';
import { getAllEloRatings } from '../firebase/eloManagement';

function EloTestPanel() {
  const [isRunning, setIsRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [raceData, setRaceData] = useState({
    "A_A": { finish: 1 },
    "B_B": { finish: 2 },
    "C_A": { finish: 3 },
    "D_B": { finish: 4 }
  });

  const runQuickTest = async () => {
    setIsRunning(true);
    setTestResult(null);

    try {
      console.log('🧪 Running ELO test with data:', raceData);
      
      // Get ratings before
      const ratingsBefore = await getAllEloRatings();
      console.log('Ratings before:', ratingsBefore);
      
      // Run ELO update
      const updateResult = await updateEloForRace(raceData);
      console.log('Update result:', updateResult);
      
      // Get ratings after
      const ratingsAfter = await getAllEloRatings();
      console.log('Ratings after:', ratingsAfter);
      
      // Calculate changes
      const changes = {};
      Object.keys(raceData).forEach(playerId => {
        const before = ratingsBefore[playerId] || 500;
        const after = ratingsAfter[playerId] || 500;
        changes[playerId] = {
          before,
          after,
          change: after - before
        };
      });
      
      setTestResult({
        success: true,
        changes,
        ratingsBefore,
        ratingsAfter
      });
      
    } catch (error) {
      console.error('ELO test failed:', error);
      setTestResult({
        success: false,
        error: error.message
      });
    } finally {
      setIsRunning(false);
    }
  };

  const updateRaceFinish = (playerId, finish) => {
    setRaceData(prev => ({
      ...prev,
      [playerId]: { finish: finish }
    }));
  };

  return (
    <Card style={{ padding: '1.5rem', marginBottom: '2rem', border: '2px solid #ffa500' }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '1.5rem'
      }}>
        <H3 style={{ 
          margin: 0, 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem' 
        }}>
          🧪 ELO Test Panel
          <Tag intent={Intent.WARNING} minimal>Testing Tool</Tag>
        </H3>
      </div>

      {/* Test Configuration */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '1rem',
        marginBottom: '1rem'
      }}>
        {Object.entries(raceData).map(([playerId, data]) => (
          <FormGroup key={playerId} label={playerId} inline>
            <HTMLSelect
              value={data.finish}
              onChange={(e) => updateRaceFinish(playerId, parseInt(e.target.value))}
              options={[
                { label: '1st Place', value: 1 },
                { label: '2nd Place', value: 2 },
                { label: '3rd Place', value: 3 },
                { label: '4th Place', value: 4 }
              ]}
            />
          </FormGroup>
        ))}
      </div>

      {/* Test Button */}
      <div style={{ marginBottom: '1rem' }}>
        <Button
          intent={Intent.PRIMARY}
          icon="play"
          onClick={runQuickTest}
          disabled={isRunning}
          loading={isRunning}
          large
        >
          Run ELO Test
        </Button>
      </div>

      {/* Test Results */}
      {testResult && (
        <Callout 
          intent={testResult.success ? Intent.SUCCESS : Intent.DANGER}
          title={testResult.success ? 'ELO Test Successful!' : 'ELO Test Failed'}
        >
          {testResult.success ? (
            <div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>ELO Changes:</strong>
              </div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '0.5rem',
                fontFamily: 'monospace',
                fontSize: '0.9rem'
              }}>
                {Object.entries(testResult.changes).map(([playerId, change]) => (
                  <div key={playerId} style={{ 
                    padding: '0.5rem',
                    background: change.change > 0 ? '#d4edda' : change.change < 0 ? '#f8d7da' : '#e2e3e5',
                    borderRadius: '4px'
                  }}>
                    <strong>{playerId}:</strong> {change.before} → {change.after} 
                    <span style={{ 
                      color: change.change > 0 ? 'green' : change.change < 0 ? 'red' : 'black',
                      fontWeight: 'bold'
                    }}>
                      ({change.change > 0 ? '+' : ''}{change.change})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <strong>Error:</strong> {testResult.error}
            </div>
          )}
        </Callout>
      )}

      {/* Info */}
      <Callout intent={Intent.PRIMARY} icon="info-sign" style={{ marginTop: '1rem' }}>
        <strong>About This Test</strong>
        <div style={{ marginTop: '0.5rem' }}>
          This test simulates a single race with the configured results and shows 
          how ELO ratings change. Use this to verify the ELO system is working correctly.
          Check the browser console for detailed logging.
        </div>
      </Callout>
    </Card>
  );
}

export default EloTestPanel;